import { createWithEqualityFn } from 'zustand/traditional';
import type { PeopleFilter } from '@/components/People/member-type';

export type MemberFilterValueKey = 'badgeIds' | 'teamIds';

export type MemberFilterValues = {
  badgeIds: string[];
  memberType: PeopleFilter;
  teamIds: string[];
};

type ActiveMemberFilter = {
  includeMemberTypes: boolean;
  includeTeams: boolean;
  organizationId: string | null;
  targetKey: string;
  teamId: string | null;
  testIDPrefix: string;
};

type MemberFilterStoreState = {
  activeFilter: ActiveMemberFilter | null;
  filtersByTargetKey: Record<string, MemberFilterValues>;
};

const EMPTY_MEMBER_FILTER: MemberFilterValues = {
  badgeIds: [],
  memberType: 'all',
  teamIds: [],
};

const initialState: MemberFilterStoreState = {
  activeFilter: null,
  filtersByTargetKey: {},
};

const useMemberFilterStore = createWithEqualityFn<MemberFilterStoreState>(
  () => initialState,
);

export default useMemberFilterStore;

export function getMemberFilterValues(
  targetKey: string,
  filtersByTargetKey: Record<string, MemberFilterValues>,
) {
  return filtersByTargetKey[targetKey] ?? EMPTY_MEMBER_FILTER;
}

export function getMemberFilterValueIds({
  filtersByTargetKey,
  targetKey,
  valueKey,
}: {
  filtersByTargetKey: Record<string, MemberFilterValues>;
  targetKey: string;
  valueKey: MemberFilterValueKey;
}) {
  return getMemberFilterValues(targetKey, filtersByTargetKey)[valueKey];
}

export function useMemberFilterValues(targetKey: string) {
  return useMemberFilterStore(
    (state) => state.filtersByTargetKey[targetKey] ?? EMPTY_MEMBER_FILTER,
  );
}

export function useMemberFilterActiveCount(targetKey: string) {
  return useMemberFilterStore((state) => {
    const filterValues = getMemberFilterValues(
      targetKey,
      state.filtersByTargetKey,
    );

    return (
      filterValues.badgeIds.length +
      filterValues.teamIds.length +
      (filterValues.memberType === 'all' ? 0 : 1)
    );
  });
}

export function useMemberFilterValueCount({
  targetKey,
  valueKey,
}: {
  targetKey: string;
  valueKey: MemberFilterValueKey;
}) {
  return useMemberFilterStore(
    (state) =>
      getMemberFilterValueIds({
        filtersByTargetKey: state.filtersByTargetKey,
        targetKey,
        valueKey,
      }).length,
  );
}

export function useMemberFilterValueSelected({
  targetKey,
  value,
  valueKey,
}: {
  targetKey: string;
  value: string;
  valueKey: MemberFilterValueKey;
}) {
  return useMemberFilterStore((state) =>
    getMemberFilterValueIds({
      filtersByTargetKey: state.filtersByTargetKey,
      targetKey,
      valueKey,
    }).includes(value),
  );
}

export function openMemberFilter({
  badgeIds,
  includeMemberTypes,
  includeTeams,
  memberType,
  organizationId,
  targetKey,
  teamId,
  teamIds,
  testIDPrefix,
}: ActiveMemberFilter & MemberFilterValues) {
  useMemberFilterStore.setState((state) => ({
    activeFilter: {
      includeTeams,
      includeMemberTypes,
      organizationId,
      targetKey,
      teamId,
      testIDPrefix,
    },
    filtersByTargetKey: {
      ...state.filtersByTargetKey,
      [targetKey]: {
        badgeIds,
        memberType: includeMemberTypes ? memberType : 'all',
        teamIds: includeTeams ? teamIds : [],
      },
    },
  }));
}

export function updateActiveMemberFilterValues({
  badgeIds,
  memberType,
  teamIds,
}: MemberFilterValues) {
  useMemberFilterStore.setState((state) => {
    const activeFilter = state.activeFilter;

    if (!activeFilter) {
      return state;
    }

    return {
      filtersByTargetKey: {
        ...state.filtersByTargetKey,
        [activeFilter.targetKey]: {
          badgeIds,
          memberType: activeFilter.includeMemberTypes ? memberType : 'all',
          teamIds: activeFilter.includeTeams ? teamIds : [],
        },
      },
    };
  });
}

export function setActiveMemberFilterValueIds({
  ids,
  valueKey,
}: {
  ids: string[];
  valueKey: MemberFilterValueKey;
}) {
  useMemberFilterStore.setState((state) => {
    const activeFilter = state.activeFilter;

    if (!activeFilter) {
      return state;
    }

    const currentValues = getMemberFilterValues(
      activeFilter.targetKey,
      state.filtersByTargetKey,
    );
    const badgeIds = valueKey === 'badgeIds' ? ids : currentValues.badgeIds;
    const teamIds = valueKey === 'teamIds' ? ids : currentValues.teamIds;

    return {
      filtersByTargetKey: {
        ...state.filtersByTargetKey,
        [activeFilter.targetKey]: {
          badgeIds,
          memberType: currentValues.memberType,
          teamIds: activeFilter.includeTeams ? teamIds : [],
        },
      },
    };
  });
}

export function setActiveMemberFilterMemberType(memberType: PeopleFilter) {
  useMemberFilterStore.setState((state) => {
    const activeFilter = state.activeFilter;

    if (!activeFilter?.includeMemberTypes) {
      return state;
    }

    const currentValues = getMemberFilterValues(
      activeFilter.targetKey,
      state.filtersByTargetKey,
    );

    return {
      filtersByTargetKey: {
        ...state.filtersByTargetKey,
        [activeFilter.targetKey]: {
          ...currentValues,
          memberType,
        },
      },
    };
  });
}

export function toggleActiveMemberFilterValue({
  selected,
  value,
  valueKey,
}: {
  selected: boolean;
  value: string;
  valueKey: MemberFilterValueKey;
}) {
  const state = useMemberFilterStore.getState();
  const activeFilter = state.activeFilter;

  if (!activeFilter) {
    return;
  }

  const currentIds = getMemberFilterValueIds({
    filtersByTargetKey: state.filtersByTargetKey,
    targetKey: activeFilter.targetKey,
    valueKey,
  });
  const nextIds = selected
    ? currentIds.includes(value)
      ? currentIds
      : [...currentIds, value]
    : currentIds.filter((currentId) => currentId !== value);

  setActiveMemberFilterValueIds({ ids: nextIds, valueKey });
}

export function clearActiveMemberFilterValues() {
  updateActiveMemberFilterValues({
    badgeIds: [],
    memberType: 'all',
    teamIds: [],
  });
}
